import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, Package } from 'lucide-react';
import { api } from '@/api/client';

interface Series {
  b2c: { prefix: string; last: number; pad: number };
  b2b: {
    prefix: string; last: number; pad: number;
    awbPrefix: string; awbLast: number; awbPad: number;   // Child + Master AWB series
    docPrefix: string; docLast: number; docPad: number;   // Document AWB series
  };
}

export function OwnCourierSeriesPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['own-courier-series'],
    queryFn: async () => (await api.get<Series>('/orders/own-courier-series')).data,
  });
  const { register, handleSubmit, reset, watch, formState: { isDirty } } = useForm<Series>({
    defaultValues: {
      b2c: { prefix: 'GNT', last: 10000000, pad: 10 },
      b2b: { prefix: 'GNT/LR/', last: 1000, pad: 6, awbPrefix: 'GNTB2B', awbLast: 121243, awbPad: 6, docPrefix: 'LR/DOC/', docLast: 121222, docPad: 6 },
    },
  });
  useEffect(() => { if (data) reset(data); }, [data, reset]);

  const save = useMutation({
    mutationFn: async (d: Series) => (await api.put('/orders/own-courier-series', d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['own-courier-series'] }); toast.success('Own-courier series saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  const b2c = watch('b2c'); const b2b = watch('b2b');

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Package size={18} /> Own Courier (Gaint) Series</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Auto-generated AWB (B2C) and LR (B2B) numbers issued when you click <strong>Book Waybill</strong> on an order assigned to the Gaint courier.
          Next number = <em>last + 1</em>, zero-padded to the given length.
        </p>
      </div>

      <SeriesCard title="B2C AWB Series">
        <Field label="Prefix"><input {...register('b2c.prefix')} className="input w-full" /></Field>
        <Field label="Last issued number"><input type="number" {...register('b2c.last', { valueAsNumber: true })} className="input w-full font-mono" /></Field>
        <Field label="Pad length"><input type="number" min={1} max={20} {...register('b2c.pad', { valueAsNumber: true })} className="input w-full" /></Field>
        <div className="col-span-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Next AWB preview</div>
          <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
            {(b2c?.prefix ?? '') + String((Number(b2c?.last) || 0) + 1).padStart(Number(b2c?.pad) || 1, '0')}
          </div>
        </div>
      </SeriesCard>

      <SeriesCard title="B2B LR Series">
        <Field label="Prefix"><input {...register('b2b.prefix')} className="input w-full" /></Field>
        <Field label="Last issued number"><input type="number" {...register('b2b.last', { valueAsNumber: true })} className="input w-full font-mono" /></Field>
        <Field label="Pad length"><input type="number" min={1} max={20} {...register('b2b.pad', { valueAsNumber: true })} className="input w-full" /></Field>
        <div className="col-span-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Next LR preview</div>
          <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
            {(b2b?.prefix ?? '') + String((Number(b2b?.last) || 0) + 1).padStart(Number(b2b?.pad) || 1, '0')}
          </div>
        </div>
      </SeriesCard>

      <SeriesCard title="B2B Child / Master AWB Series">
        <Field label="Prefix"><input {...register('b2b.awbPrefix')} className="input w-full" /></Field>
        <Field label="Last issued number"><input type="number" {...register('b2b.awbLast', { valueAsNumber: true })} className="input w-full font-mono" /></Field>
        <Field label="Pad length"><input type="number" min={1} max={20} {...register('b2b.awbPad', { valueAsNumber: true })} className="input w-full" /></Field>
        <div className="col-span-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Next box/master AWB preview</div>
          <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
            {(b2b?.awbPrefix ?? '') + String((Number(b2b?.awbLast) || 0) + 1).padStart(Number(b2b?.awbPad) || 1, '0')}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Used for the Master AWB and every child box AWB (one per box, plus one master).</p>
        </div>
      </SeriesCard>

      <SeriesCard title="B2B Document AWB Series">
        <Field label="Prefix"><input {...register('b2b.docPrefix')} className="input w-full" /></Field>
        <Field label="Last issued number"><input type="number" {...register('b2b.docLast', { valueAsNumber: true })} className="input w-full font-mono" /></Field>
        <Field label="Pad length"><input type="number" min={1} max={20} {...register('b2b.docPad', { valueAsNumber: true })} className="input w-full" /></Field>
        <div className="col-span-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Next document AWB preview</div>
          <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
            {(b2b?.docPrefix ?? '') + String((Number(b2b?.docLast) || 0) + 1).padStart(Number(b2b?.docPad) || 1, '0')}
          </div>
        </div>
      </SeriesCard>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button type="submit" disabled={!isDirty || save.isPending}
          className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>

      <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <strong>Warning:</strong> Lowering the "last" value risks duplicate AWB numbers if previously-issued numbers are re-issued.
        Only edit when initializing the system or after deliberate reset.
      </div>
    </form>
  );
}

function SeriesCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="text-sm font-semibold text-slate-900 mb-3">{title}</div>
      <div className="grid grid-cols-3 gap-4">{children}</div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
